import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  perPage?: number;
  perPageOptions?: readonly number[];
  onPerPageChange?: (perPage: number) => void;
  // Singular/plural noun for the count summary, e.g. "device"/"devices".
  itemNoun?: { singular: string; plural: string };
  // Extra control slot rendered after the per-page selector (with a divider).
  // Used for inline sort selectors etc. so we don't burn a whole row above
  // the list on tiny pagination strips.
  extra?: React.ReactNode;
  className?: string;
}

// Sliding-window page numbers with ellipsis. Returns at most 7 entries
// regardless of totalPages so the row stays a fixed visual width.
function getPageWindow(current: number, total: number): (number | 'ellipsis-l' | 'ellipsis-r')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | 'ellipsis-l' | 'ellipsis-r')[] = [1];

  if (current > 4) {
    pages.push('ellipsis-l');
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  for (let p = start; p <= end; p++) {
    pages.push(p);
  }

  if (current < total - 3) {
    pages.push('ellipsis-r');
  }

  pages.push(total);
  return pages;
}

// Always-visible pagination row with numbered page buttons, prev/next
// arrows, an optional per-page selector, and a "Showing X–Y of N items"
// count line. At 1 page the controls render in a disabled state so the
// affordance stays predictable across all states.
export function Pagination({
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  perPage,
  perPageOptions,
  onPerPageChange,
  itemNoun = { singular: 'item', plural: 'items' },
  extra,
  className,
}: PaginationProps) {
  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrent = Math.min(Math.max(1, currentPage), safeTotalPages);
  const window = getPageWindow(safeCurrent, safeTotalPages);

  const startIdx = totalCount === 0 ? 0 : (safeCurrent - 1) * (perPage ?? 0) + 1;
  const endIdx = perPage ? Math.min(safeCurrent * perPage, totalCount) : totalCount;

  const showPerPage = !!perPage && !!perPageOptions && !!onPerPageChange;

  return (
    <nav
      aria-label="Pagination"
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-1',
        className,
      )}
    >
      {/* Left side: count summary + per-page selector */}
      <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500 dark:text-gray-400">
        <p className="tabular-nums">
          {totalCount === 0 ? (
            <>No {itemNoun.plural}</>
          ) : (
            <>
              Showing{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {startIdx.toLocaleString()}–{endIdx.toLocaleString()}
              </span>{' '}
              of{' '}
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {totalCount.toLocaleString()}
              </span>{' '}
              {totalCount === 1 ? itemNoun.singular : itemNoun.plural}
            </>
          )}
        </p>
        {showPerPage && (
          <div className="flex items-center gap-2">
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <label className="inline-flex items-center gap-2">
              <span className="sr-only sm:not-sr-only">Show</span>
              <div className="w-[88px]">
                <Select
                  value={String(perPage)}
                  onChange={(v) => onPerPageChange!(Number(v))}
                  options={perPageOptions!.map((n) => ({ value: String(n), label: String(n) }))}
                />
              </div>
              <span>per page</span>
            </label>
          </div>
        )}
        {extra && (
          <div className="flex items-center gap-2">
            <span className="text-gray-300 dark:text-gray-600">·</span>
            {extra}
          </div>
        )}
      </div>

      {/* Right side: page-number + arrow buttons */}
      <div className="flex items-center gap-1">
        <PageButton
          aria-label="Previous page"
          disabled={safeCurrent <= 1}
          onClick={() => onPageChange(safeCurrent - 1)}
        >
          <ChevronLeft className="w-4 h-4" />
        </PageButton>
        {window.map((entry, i) =>
          entry === 'ellipsis-l' || entry === 'ellipsis-r' ? (
            <span
              key={entry + i}
              aria-hidden="true"
              className="px-1.5 text-gray-400 dark:text-gray-500 select-none"
            >
              …
            </span>
          ) : (
            <PageButton
              key={entry}
              active={entry === safeCurrent}
              aria-label={`Go to page ${entry}`}
              aria-current={entry === safeCurrent ? 'page' : undefined}
              onClick={() => onPageChange(entry)}
            >
              {entry}
            </PageButton>
          ),
        )}
        <PageButton
          aria-label="Next page"
          disabled={safeCurrent >= safeTotalPages}
          onClick={() => onPageChange(safeCurrent + 1)}
        >
          <ChevronRight className="w-4 h-4" />
        </PageButton>
      </div>
    </nav>
  );
}

interface PageButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

function PageButton({ active, className, disabled, children, ...props }: PageButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center min-w-[34px] h-9 px-2.5 rounded-lg text-sm font-medium tabular-nums transition-colors',
        'border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40',
        active
          ? 'bg-primary-600 text-white border-primary-600 shadow-sm dark:bg-primary-500 dark:border-primary-500'
          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-gray-700',
        disabled && 'opacity-40 cursor-not-allowed hover:bg-white dark:hover:bg-gray-800 hover:border-gray-200 dark:hover:border-gray-700',
        className,
      )}
      {...props}
    >
      {children}
    </button>
  );
}
